/**
 * Здоровье локального хранилища.
 *
 * IndexedDB отказывает чаще, чем принято думать: приватное окно, переполненный
 * диск, вторая вкладка, держащая старую версию схемы, откат приложения на
 * предыдущую сборку. До этого модуля любой такой отказ выглядел одинаково —
 * бесконечные скелетоны без единого слова о том, что случилось, а упавшая
 * миграция закрывала приложение навсегда: `db.open()` отклонялся при каждом
 * запуске, и сбросить это состояние человеку было нечем.
 *
 * Модуль ничего не чинит сам. Его задача — назвать причину и предложить
 * действие, которое действительно помогает именно в этом случае.
 */

import { record } from '@/shared/lib/diagnostics';

import { db } from './database';

export type StorageFailure =
  /** Другая вкладка держит старую версию схемы и мешает обновлению. */
  | 'blocked'
  /** В браузере данные новее, чем ожидает эта сборка приложения. */
  | 'version'
  /** Кончилось место или браузер запретил хранилище (приватное окно). */
  | 'quota'
  /** Всё остальное: повреждённая база, отказ миграции. */
  | 'unknown';

export interface StorageProblem {
  kind: StorageFailure;
  title: string;
  description: string;
  /** Что человеку сделать прямо сейчас. */
  hint: string;
}

const PROBLEMS: Record<StorageFailure, Omit<StorageProblem, 'kind'>> = {
  blocked: {
    title: 'Приложение открыто в другой вкладке',
    description:
      'Там работает предыдущая версия и не даёт обновить хранилище. Данные целы — нужно лишь закрыть остальные вкладки.',
    hint: 'Закройте другие вкладки приложения и обновите эту.',
  },
  version: {
    title: 'Данные новее приложения',
    description:
      'На этом устройстве уже работала более новая версия приложения. Старая сборка её данные открыть не может, чтобы ничего не испортить.',
    hint: 'Обновите страницу — браузер загрузит актуальную версию.',
  },
  quota: {
    title: 'Браузер не даёт сохранять данные',
    description:
      'Закончилось место на устройстве, либо страница открыта в приватном окне, где хранилище запрещено.',
    hint: 'Освободите место на устройстве или откройте приложение в обычном окне.',
  },
  unknown: {
    title: 'Не удалось открыть хранилище',
    description:
      'База данных на устройстве не открывается. Данные, скорее всего, целы, но приложению нужен доступ к ним.',
    hint: 'Обновите страницу. Если не поможет — закройте другие вкладки приложения и попробуйте снова.',
  },
};

/** Определяет причину отказа по ошибке Dexie или DOMException. */
export function classifyStorageError(error: unknown): StorageFailure {
  const name = error instanceof Error ? error.name : '';
  const message = error instanceof Error ? error.message : String(error);

  if (name === 'VersionError' || message.includes('less than the existing version')) return 'version';
  if (name === 'QuotaExceededError' || message.includes('quota')) return 'quota';
  if (name === 'BlockedError' || message.includes('blocked')) return 'blocked';
  return 'unknown';
}

export function describeStorageError(error: unknown): StorageProblem {
  const kind = classifyStorageError(error);
  return { kind, ...PROBLEMS[kind] };
}

/**
 * Открывает базу и переводит отказ в объяснимую причину.
 *
 * Вызывается один раз при старте: если хранилище недоступно, показывать
 * приложение бессмысленно — все экраны будут пустыми, и человек решит, что
 * потерял данные.
 */
export async function openDatabase(): Promise<StorageProblem | null> {
  try {
    await db.open();
    return null;
  } catch (error) {
    record('error', 'хранилище', error);
    return describeStorageError(error);
  }
}

/**
 * Подписывает базу на события, которые иначе выглядят как зависание.
 *
 * `blocked` — обновление схемы ждёт, пока закроется другая вкладка.
 * `versionchange` — другая вкладка уже обновляет схему; соединение надо
 * отпустить, иначе она будет ждать нас бесконечно.
 */
export function watchStorage(onProblem: (problem: StorageProblem) => void): () => void {
  const onBlocked = () => onProblem({ kind: 'blocked', ...PROBLEMS.blocked });

  const onVersionChange = () => {
    db.close();
    onProblem({ kind: 'version', ...PROBLEMS.version });
  };

  db.on('blocked', onBlocked);
  db.on('versionchange', onVersionChange);

  return () => {
    db.on('blocked').unsubscribe(onBlocked);
    db.on('versionchange').unsubscribe(onVersionChange);
  };
}

/**
 * Просит браузер не вытеснять данные.
 *
 * Без этого IndexedDB — «лучшее усилие»: при нехватке места браузер вправе
 * очистить хранилище сайта молча. Для приложения, где журнал приёмов лекарств
 * и замеры давления существуют в единственном экземпляре, это потеря без
 * предупреждения. Разрешение выдаётся тихо и обычно тем, кто добавил
 * приложение на экран или пользуется им регулярно.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;

  try {
    if (await navigator.storage.persisted?.()) return true;
    return await navigator.storage.persist();
  } catch {
    // Отказ в разрешении — не повод ломать запуск: приложение продолжает
    // работать, просто данные остаются вытесняемыми.
    return false;
  }
}
